<template>
  <div class="sci-view-container">
    <!-- Header -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button @click="$router.push({ name: 'Gestionale' })" class="sci-btn-back" title="Torna al Gestionale">
          <i class="fas fa-arrow-left"></i>
          <span>Gestionale</span>
        </button>
        <div class="sci-title-group">
          <div class="sci-badge-tag">
            <i class="fas fa-cash-register"></i> Cassa & Fiscale
          </div>
          <h1 class="sci-view-title">Documenti Fiscali & Scontrini RT</h1>
          <p class="sci-view-subtitle">Registro telematico delle ricevute emesse, progressivi e gestione annullamento</p>
        </div>
      </div>
      <div class="sci-header-actions">
        <button class="sci-btn sci-btn-secondary" @click="fetchDocumenti" :disabled="loading">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          <span>Aggiorna</span>
        </button>
      </div>
    </header>

    <!-- Bento KPI Grid -->
    <section class="sci-kpi-grid">
      <div class="sci-kpi-card primary">
        <div class="kpi-icon-wrap">
          <i class="fas fa-euro-sign"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Totale Incasso Lordo</span>
          <span class="kpi-value">€ {{ totaleValido.toFixed(2) }}</span>
          <span class="kpi-sub">Documenti attivi nel periodo</span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap blue">
          <i class="fas fa-receipt"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Documenti Totali</span>
          <span class="kpi-value">{{ documenti.length }}</span>
          <span class="kpi-sub">Emesse nel periodo selezionato</span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap green">
          <i class="fas fa-check-circle"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Ricevute Attive</span>
          <span class="kpi-value">{{ documentiAttiviCount }}</span>
          <span class="kpi-sub">Scontrini validi registrati</span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap red">
          <i class="fas fa-ban"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Scontrini Annullati</span>
          <span class="kpi-value">{{ documentiAnnullatiCount }}</span>
          <span class="kpi-sub">Valore annullato: € {{ totaleAnnullato.toFixed(2) }}</span>
        </div>
      </div>
    </section>

    <!-- Controls Bar -->
    <section class="sci-card sci-controls-card">
      <div class="sci-controls-bar">
        <!-- Date range -->
        <div class="sci-filter-group">
          <div class="sci-input-field">
            <label><i class="far fa-calendar-alt"></i> Da:</label>
            <input type="date" v-model="filters.dataDal" @change="fetchDocumenti" class="sci-input" />
          </div>
          <div class="sci-input-field">
            <label><i class="far fa-calendar-alt"></i> A:</label>
            <input type="date" v-model="filters.dataAl" @change="fetchDocumenti" class="sci-input" />
          </div>
        </div>

        <!-- Quick Presets -->
        <div class="sci-presets-group">
          <button class="sci-preset-chip" :class="{ active: currentPreset === 'today' }" @click="setPreset('today')">
            Oggi
          </button>
          <button class="sci-preset-chip" :class="{ active: currentPreset === 'week' }" @click="setPreset('week')">
            Ultimi 7 gg
          </button>
          <button class="sci-preset-chip" :class="{ active: currentPreset === 'month' }" @click="setPreset('month')">
            Mese
          </button>
        </div>

        <!-- Search Bar -->
        <div class="sci-search-field">
          <i class="fas fa-search sci-search-icon"></i>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Cerca progressivo o etichetta..."
            class="sci-input search-input"
          />
          <button v-if="searchQuery" class="sci-clear-btn" @click="searchQuery = ''">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </section>

    <!-- Documents Table Card -->
    <section class="sci-card sci-table-card">
      <div class="table-responsive" v-if="filteredDocumenti.length">
        <table class="sci-table">
          <thead>
            <tr>
              <th>Data & Ora</th>
              <th>Progressivo RT</th>
              <th>Etichetta / Cassa</th>
              <th>Totale Lordo</th>
              <th>Stato Fiscale</th>
              <th class="text-end">Azioni</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="doc in filteredDocumenti" :key="doc.progressivo" :class="{ 'row-cancelled': doc.stato !== 1 }">
              <td>
                <div class="datetime-cell">
                  <i class="far fa-clock"></i>
                  <span>{{ formatDate(doc.dataCreazione) }}</span>
                </div>
              </td>
              <td>
                <span class="code-badge">
                  <i class="fas fa-hashtag"></i> {{ doc.progressivo }}
                </span>
              </td>
              <td>
                <span class="label-badge">{{ doc.etichetta || 'Registratore Principale' }}</span>
              </td>
              <td>
                <span class="price-badge" :class="{ 'price-cancelled': doc.stato !== 1 }">
                  € {{ (doc.totale || 0).toFixed(2) }}
                </span>
              </td>
              <td>
                <span :class="doc.stato === 1 ? 'sci-pill-success' : 'sci-pill-danger'">
                  <i :class="doc.stato === 1 ? 'fas fa-check-circle' : 'fas fa-ban'"></i>
                  {{ doc.stato === 1 ? 'Attivo' : 'Annullato' }}
                </span>
              </td>
              <td class="text-end">
                <button
                  v-if="doc.stato === 1"
                  @click="confirmCancel(doc)"
                  class="sci-btn-action danger"
                  title="Annulla scontrino fiscale"
                >
                  <i class="fas fa-trash-alt"></i> Annulla
                </button>
                <span v-else class="text-muted-tag">
                  <i class="fas fa-lock"></i> Annullato
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Loading State -->
      <div v-else-if="loading" class="sci-state-feedback">
        <div class="sci-spinner"></div>
        <p class="state-title">Caricamento registri fiscali in corso...</p>
        <p class="state-subtitle">Interrogazione del registratore telematico...</p>
      </div>

      <!-- Empty State -->
      <div v-else class="sci-state-feedback">
        <div class="empty-icon-wrap">
          <i class="fas fa-receipt"></i>
        </div>
        <p class="state-title">Nessun documento fiscale trovato</p>
        <p class="state-subtitle">
          Non risultano scontrini emessi per i filtri temporali selezionati.
        </p>
      </div>
    </section>
  </div>
</template>

<script>
import { store } from '../store/store.js'

export default {
  name: 'DocumentiFiscali',
  data() {
    return {
      store,
      documenti: [],
      loading: false,
      searchQuery: '',
      currentPreset: 'today',
      filters: {
        dataDal: new Date().toISOString().split('T')[0],
        dataAl: new Date().toISOString().split('T')[0]
      }
    }
  },
  computed: {
    totaleValido() {
      return this.documenti
        .filter(d => d.stato === 1)
        .reduce((sum, d) => sum + (Number(d.totale) || 0), 0);
    },
    totaleAnnullato() {
      return this.documenti
        .filter(d => d.stato !== 1)
        .reduce((sum, d) => sum + (Number(d.totale) || 0), 0);
    },
    documentiAttiviCount() {
      return this.documenti.filter(d => d.stato === 1).length;
    },
    documentiAnnullatiCount() {
      return this.documenti.filter(d => d.stato !== 1).length;
    },
    filteredDocumenti() {
      if (!this.searchQuery.trim()) return this.documenti;
      const q = this.searchQuery.toLowerCase().trim();
      return this.documenti.filter(doc => {
        const prog = String(doc.progressivo || '').toLowerCase();
        const label = String(doc.etichetta || '').toLowerCase();
        return prog.includes(q) || label.includes(q);
      });
    }
  },
  methods: {
    getToken() {
      try {
        if (!localStorage.token) return '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (parsed.token || localStorage.token);
      } catch (e) {
        return localStorage.token || '';
      }
    },
    setPreset(preset) {
      this.currentPreset = preset;
      const today = new Date();
      const end = today.toISOString().split('T')[0];
      let start = end;

      if (preset === 'week') {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        start = d.toISOString().split('T')[0];
      } else if (preset === 'month') {
        const d = new Date(today.getFullYear(), today.getMonth(), 1);
        start = d.toISOString().split('T')[0];
      }

      this.filters.dataDal = start;
      this.filters.dataAl = end;
      this.fetchDocumenti();
    },
    async fetchDocumenti() {
      this.loading = true;
      const token = this.getToken();
      try {
        const payload = {
          filters: {
            dataDal: this.filters.dataDal + 'T00:00:00',
            dataAl: this.filters.dataAl + 'T23:59:59'
          }
        };
        const config = {
          headers: {
            authorization: `Bearer ${token}`
          }
        };
        const response = await this.axios.post(this.store.path + 'scontrino/list', payload, config);
        this.documenti = response.data?.vendita || [];
      } catch (error) {
        console.error("Errore fetch documenti:", error);
      } finally {
        this.loading = false;
      }
    },
    async confirmCancel(doc) {
      if (!confirm(`Sei sicuro di voler procedere con l'annullamento fiscale del documento n. ${doc.progressivo}?`)) {
        return;
      }
      const token = this.getToken();
      try {
        await this.axios.post(this.store.path + 'scontrino/cancel', {
          progressivo: doc.progressivo,
          data: doc.dataCreazione ? doc.dataCreazione.split('T')[0] : '',
          totale: doc.totale
        }, {
          headers: { authorization: `Bearer ${token}` }
        });
        
        alert("Documento fiscale annullato con successo!");
        this.fetchDocumenti();
      } catch (error) {
        console.error("Errore annullamento:", error);
        alert("Errore nell'annullamento del documento: " + (error.response?.data?.message || error.message));
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      const date = new Date(dateStr);
      return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) + 
        ' ' + date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    }
  },
  mounted() {
    this.fetchDocumenti();
  }
}
</script>

<style scoped>
.sci-view-container {
  min-height: 100vh;
  background-color: #f1f5f9;
  padding: 24px 32px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #0f172a;
  box-sizing: border-box;
}

/* Header */
.sci-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  color: #334155;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.sci-btn-back:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-badge-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #0284c7;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.sci-view-title {
  font-size: 1.65rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1.2;
}

.sci-view-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 4px 0 0 0;
}

/* Buttons */
.sci-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.sci-btn-secondary {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.sci-btn-secondary:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #94a3b8;
}

/* Bento KPI Cards */
.sci-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.sci-kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: transform 0.2s, box-shadow 0.2s;
}

.sci-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px -2px rgba(0, 0, 0, 0.08);
}

.sci-kpi-card.primary {
  border-left: 4px solid #10b981;
}

.kpi-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: #ecfdf5;
  color: #059669;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  flex-shrink: 0;
}

.kpi-icon-wrap.blue {
  background: #eff6ff;
  color: #2563eb;
}

.kpi-icon-wrap.green {
  background: #f0fdf4;
  color: #16a34a;
}

.kpi-icon-wrap.red {
  background: #fef2f2;
  color: #dc2626;
}

.kpi-meta {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
}

.kpi-value {
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
  margin: 2px 0;
}

.kpi-sub {
  font-size: 0.75rem;
  color: #94a3b8;
}

/* Card General */
.sci-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
  overflow: hidden;
}

/* Controls Bar */
.sci-controls-card {
  padding: 16px 20px;
}

.sci-controls-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.sci-filter-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sci-input-field {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sci-input-field label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
}

.sci-input {
  min-height: 42px;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #0f172a;
  background: #f8fafc;
  outline: none;
  transition: all 0.2s;
}

.sci-input:focus {
  border-color: #2563eb;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.sci-presets-group {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 10px;
}

.sci-preset-chip {
  min-height: 34px;
  padding: 0 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.sci-preset-chip.active {
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.sci-search-field {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 260px;
}

.sci-search-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  font-size: 0.9rem;
}

.search-input {
  padding-left: 36px;
  padding-right: 32px;
  width: 100%;
}

.sci-clear-btn {
  position: absolute;
  right: 10px;
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
}

/* Table */
.sci-table-card {
  padding: 0;
}

.table-responsive {
  overflow-x: auto;
}

.sci-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.sci-table th {
  background: #f8fafc;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
}

.sci-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  font-size: 0.95rem;
  vertical-align: middle;
}

.sci-table tbody tr {
  transition: background 0.15s;
}

.sci-table tbody tr:hover {
  background: #f8fafc;
}

.row-cancelled {
  background: #fef2f2 !important;
  opacity: 0.85;
}

.datetime-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.datetime-cell i {
  color: #94a3b8;
}

.code-badge {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-weight: 700;
  color: #1e293b;
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 6px;
}

.label-badge {
  color: #475569;
  font-weight: 500;
}

.price-badge {
  font-weight: 800;
  color: #059669;
  font-size: 1.05rem;
}

.price-cancelled {
  color: #dc2626 !important;
  text-decoration: line-through;
}

/* Status Pills */
.sci-pill-success {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 50px;
  background: #dcfce7;
  color: #15803d;
  font-size: 0.85rem;
  font-weight: 700;
}

.sci-pill-danger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 50px;
  background: #fee2e2;
  color: #b91c1c;
  font-size: 0.85rem;
  font-weight: 700;
}

.sci-btn-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.sci-btn-action.danger {
  background: #fee2e2;
  color: #b91c1c;
}

.sci-btn-action.danger:hover {
  background: #ef4444;
  color: #ffffff;
}

.text-muted-tag {
  font-size: 0.85rem;
  color: #94a3b8;
  font-weight: 600;
}

/* Feedback States */
.sci-state-feedback {
  padding: 60px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.sci-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: #f1f5f9;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin-bottom: 16px;
}

.state-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.state-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0;
}
</style>
